import React, { useState, useEffect } from 'react';
import { CartItem as CartItemType, Product } from '../../types';
import { formatFulfillmentWindow } from '../../constants/fulfillmentWindows';
import { useCartOperations, useCartToast } from '../../contexts';
import { getProduct } from '../../services/firebase';
import './CartItem.css';

interface CartItemProps {
  item: CartItemType;
  onRemove?: (itemId: string) => void;
  onQuantityChange?: (itemId: string, quantity: number) => void;
  showRemoveButton?: boolean;
  showQuantityControls?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
}

export const CartItem: React.FC<CartItemProps> = ({
  item,
  onRemove,
  onQuantityChange,
  showRemoveButton = true,
  showQuantityControls = true,
  variant = 'default'
}) => {
  const { removeItem, updateQuantity } = useCartOperations();
  const { showRemoveFromCartSuccess, showQuantityUpdated } = useCartToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  // Fetch product data to check if it's a dessert and get quantity tiers
  useEffect(() => {
    const fetchProduct = async () => {
      if (!item.productId) return;
      
      setIsLoadingProduct(true);
      try {
        const productData = await getProduct(item.productId);
        setProduct(productData);
      } catch (error) {
        console.error('Error fetching product for cart item:', error);
      } finally {
        setIsLoadingProduct(false);
      }
    };

    fetchProduct();
  }, [item.productId]);

  // Check if this is a dessert product
  const isDessert = product?.collection === 'Desserts' || product?.collectionId === 'desserts';
  
  // Get quantity tiers for the selected size
  const selectedSize = product?.sizes?.find(size => size.name === item.size);
  const quantityTiers = selectedSize?.quantityTiers || [];

  const handleRemove = () => {
    if (onRemove) {
      onRemove(item.id);
    } else {
      removeItem(item.id);
      showRemoveFromCartSuccess(item.name);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) {
      handleRemove();
      return;
    }

    // Note: For desserts with quantity tiers, price recalculation should happen
    // in the cart operations. For now, we just update the quantity.
    // The price will need to be recalculated based on the new tier.
    if (onQuantityChange) {
      onQuantityChange(item.id, newQuantity);
    } else {
      updateQuantity(item.id, newQuantity);
      showQuantityUpdated(item.name, newQuantity);
    }
  };

  const handleIncrement = () => {
    handleQuantityChange(item.quantity + 1);
  };

  const handleDecrement = () => {
    handleQuantityChange(item.quantity - 1);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const renderCustomizationDetails = () => {
    if (variant === 'compact') return null;

    const details = [];
    
    if (item.size) details.push(`Size: ${item.size}`);
    if (item.flavors && item.flavors.length > 0) {
      details.push(`Flavors: ${item.flavors.join(', ')}`);
    }
    if (item.shape) details.push(`Shape: ${item.shape}`);
    if (item.layers) details.push(`Layers: ${item.layers}`);
    if (item.filling) details.push(`Filling: ${item.filling}`);
    if (item.icing) details.push(`Icing: ${item.icing}`);
    if (item.baseColor) details.push(`Base: ${item.baseColor}`);
    if (item.pipingColor) details.push(`Piping: ${item.pipingColor}`);
    if (item.addOns && item.addOns.length > 0) {
      details.push(`Add-ons: ${item.addOns.map(addOn => addOn.split('+€')[0].trim()).join(', ')}`);
    }
    if (item.matchingCupcakes && item.matchingCupcakes.length > 0) {
      details.push(`Matching Cupcakes: ${item.matchingCupcakes.map(cupcake => cupcake.split('+€')[0].trim()).join(', ')}`);
    }
    if (item.hasImageOnCake) {
      details.push('Image on cake: Yes');
    }
    
    // Add delivery information
    if (item.deliveryOption) {
      if (item.deliveryOption === 'collection') {
        details.push('Collection: Pick up from store');
      } else if (item.deliveryOption === 'delivery' && item.deliveryDate) {
        const deliveryDate = item.deliveryDate instanceof Date ? item.deliveryDate : new Date(item.deliveryDate);
        details.push(`Delivery: ${deliveryDate.toLocaleDateString('en-IE', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        })}`);
      } else if (item.deliveryOption === 'delivery') {
        details.push('Delivery: Date to be selected');
      }
    }
    if (item.timeWindowStart && item.timeWindowEnd) {
      const windowLabel = formatFulfillmentWindow(item.timeWindowStart, item.timeWindowEnd);
      details.push(`Window: ${windowLabel}`);
    }

    if (details.length === 0) return null;

    return (
      <div className="cart-item__details">
        {details.map((detail, index) => (
          <span key={index} className="cart-item__detail">
            {detail}
          </span>
        ))}
      </div>
    );
  };

  const renderSpecialInstructions = () => {
    if (variant === 'compact' || !item.specialInstructions) return null;

    return (
      <div className="cart-item__instructions">
        <strong>Message on cake:</strong> {item.specialInstructions}
      </div>
    );
  };

  const renderPriceBreakdown = () => {
    if (variant === 'compact') return null;

    const hasAdditions = item.flavorAdditionalPrice > 0 || 
                        item.fillingAdditionalPrice > 0 || 
                        item.icingAdditionalPrice > 0 ||
                        item.addOnsAdditionalPrice > 0 ||
                        item.matchingCupcakesAdditionalPrice > 0 ||
                        (item.baseColorAdditionalPrice || 0) > 0;

    if (!hasAdditions) return null;

    return (
      <div className="cart-item__price-breakdown">
        <div className="cart-item__price-line">
          <span>Base Price:</span>
          <span>{formatPrice(item.basePrice)}</span>
        </div>
        {item.flavorAdditionalPrice > 0 && (
          <div className="cart-item__price-line">
            <span>Flavors:</span>
            <span>+{formatPrice(item.flavorAdditionalPrice)}</span>
          </div>
        )}
        {item.fillingAdditionalPrice > 0 && (
          <div className="cart-item__price-line">
            <span>Filling:</span>
            <span>+{formatPrice(item.fillingAdditionalPrice)}</span>
          </div>
        )}
        {item.icingAdditionalPrice > 0 && (
          <div className="cart-item__price-line">
            <span>Icing:</span>
            <span>+{formatPrice(item.icingAdditionalPrice)}</span>
          </div>
        )}
        {item.addOnsAdditionalPrice > 0 && (
          <div className="cart-item__price-line">
            <span>Add-ons:</span>
            <span>+{formatPrice(item.addOnsAdditionalPrice)}</span>
          </div>
        )}
        {item.matchingCupcakesAdditionalPrice > 0 && (
          <div className="cart-item__price-line">
            <span>Matching Cupcakes:</span>
            <span>+{formatPrice(item.matchingCupcakesAdditionalPrice)}</span>
          </div>
        )}
        {(item.baseColorAdditionalPrice || 0) > 0 && (
          <div className="cart-item__price-line">
            <span>Base colour:</span>
            <span>+{formatPrice(item.baseColorAdditionalPrice)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`cart-item cart-item--${variant}`}>
      <div className="cart-item__image">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="cart-item__img"
            loading="lazy"
          />
        ) : (
          <div className="cart-item__placeholder">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 16L8.5 10.5L13 14L20 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="cart-item__content">
        <div className="cart-item__header">
          <h3 className="cart-item__name">{item.name}</h3>
          {showRemoveButton && (
            <button
              className="cart-item__remove"
              onClick={handleRemove}
              aria-label={`Remove ${item.name} from cart`}
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {renderCustomizationDetails()}
        {renderSpecialInstructions()}
        {renderPriceBreakdown()}

        <div className="cart-item__footer">
          <div className="cart-item__quantity">
            {showQuantityControls ? (
              // For desserts, show quantity tier selector instead of plus/minus buttons
              isDessert && quantityTiers.length > 0 ? (
                <div className="quantity-tier-selector">
                  <div className="quantity-tier-options">
                    {quantityTiers.map((tier) => (
                      <button
                        key={tier.quantity}
                        className={`quantity-tier-option ${item.quantity === tier.quantity ? 'selected' : ''}`}
                        onClick={() => handleQuantityChange(tier.quantity)}
                        type="button"
                        aria-label={`Select quantity ${tier.quantity}`}
                      >
                        {tier.quantity} pcs
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="quantity-controls">
                  <button
                    className="quantity-controls__button"
                    onClick={handleDecrement}
                    aria-label="Decrease quantity"
                    type="button"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <span className="quantity-controls__value">{item.quantity}</span>
                  <button
                    className="quantity-controls__button"
                    onClick={handleIncrement}
                    aria-label="Increase quantity"
                    type="button"
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )
            ) : (
              <span className="cart-item__quantity-text">Qty: {item.quantity}</span>
            )}
          </div>

          <div className="cart-item__pricing">
            {/* Hide "price per item" for desserts */}
            {!isDessert && (
              <div className="cart-item__unit-price">
                {formatPrice(item.unitPrice)} each
              </div>
            )}
            <div className="cart-item__total-price">
              {formatPrice(item.totalPrice)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
